/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Result, Button, Spin } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { useMutation } from 'react-query';
import { Redirect, Route, Switch } from 'react-router-dom';

import { FileProgressPanel } from '~components/molecules/FileProgressPanel/FileProgressPanel';
import AboveNav from '~components/organisms/AboveNav/AboveNav';
import LeftNav from '~components/organisms/LeftNav/LeftNav';
import RightDisplay from '~components/organisms/RightDisplay/RightDisplay';
import RightDisplaySu from '~components/organisms/RightDisplaySu/RightDisplaySu';
import RightDisplaySuHome from '~components/organisms/RightDisplaySuHome/RightDisplaySuHome';
import { CommitsModal } from '~components/popups-modals/Commits.modal';
import { LargeFilesModal } from '~components/popups-modals/LargeFiles.modal';
import { __DEV__ } from '~src/config';
import { apiCweb, getNewToken } from '~src/services/api/api-cweb';
import { fadeInKeyframes } from '~src/styles/animations';
import AppContext from '~src/contexts/AppContext';

import FileProgressContext, {
  FileProgressCtx,
} from '~src/contexts/FileProgressContext';
import LargeFilesContext, {
  LargeFilesCtx,
} from '~src/contexts/LargeFilesContext';
import { ModalsProvider } from '~src/contexts/ModalsContext';
import { ActionModalsContainer } from '~components/molecules/ActionModalsContainer/ActionModalsContainer';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import UploadContext, { UploadCtx } from '~src/contexts/UploadContext';
import ShowUploadDropZoneContext, {
  ShowUploadDropZoneCtx,
} from '~src/contexts/ShowUploadDropZone';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FileToViewProvider } from '~src/contexts/FileToViewContext';
import SidebarContext from '~src/contexts/SidebarContext';
import RightSideViewHome from '~components/molecules/RightSideViewHome/RightSideViewHome';
import { PrimaryViewerPreviewWrapper } from '~components/atoms/Viewers/SVFViewer/PrimaryViewerPreviewWrapper';

const breakpoints = [576, 768, 992, 1200];

const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`);

const ipdMiniMediaQ = `@media (device-width : 768px) and (resolution: 163dpi)`;
const ipdProMediaQ = `@media (height: 1024px) and (width: 1366px) and (-webkit-min-device-pixel-ratio: 1.5) and (orientation: landscape), (width: 1024px) and (height: 1366px) and (-webkit-min-device-pixel-ratio: 1.5) and (orientation: portrait)`;

/** /projects navigation */
const ProjectsRoot = () => {
  const project = useProject();
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [doGetNewToken, getNewTokenMutation] = useMutation(getNewToken);
  // set the inital App Context to 'normal' for the regular view of the app
  // the 3 states are 'normal', 'time-machine', 'super-user'
  // TODO: replace with query param

  const [appContextState, setAppContextState] = useState('normal');
  const appContextStateChanged = useMemo(
    () => ({ appContextState, setAppContextState }),
    [appContextState, setAppContextState]
  );

  const [fileProgressContext, setFileProgressContext] = useState<
    FileProgressCtx['fileProgressContext']
  >([]);
  const fileProgressChanged = useMemo(
    () => ({ fileProgressContext, setFileProgressContext }),
    [fileProgressContext, setFileProgressContext]
  );

  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const sidebarContextValue = useMemo(
    () => ({ isSidebarMinimized, setIsSidebarMinimized }),
    [isSidebarMinimized, setIsSidebarMinimized]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1100px)');

    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      setIsSidebarMinimized(e.matches);
    };

    setIsSidebarMinimized(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  const [largeFilesContext, setLargeFilesContext] = useState<
    LargeFilesCtx['largeFilesContext']
  >([]);
  const largeFilesChanged = useMemo(
    () => ({ largeFilesContext, setLargeFilesContext }),
    [largeFilesContext, setLargeFilesContext]
  );

  // context of files ready to be uploaded
  const [uploadContext, setUploadContext] = useState<
    UploadCtx['uploadContext']
  >([]);
  const uploadContextChanged = useMemo(
    () => ({ uploadContext, setUploadContext }),
    [uploadContext, setUploadContext]
  );

  const [showUploadZone, setShowUploadZone] = useState<
    ShowUploadDropZoneCtx['showUploadZone']
  >(false);
  const setShowUploadZoneContextChanged = useMemo(
    () => ({ showUploadZone, setShowUploadZone }),
    [showUploadZone, setShowUploadZone]
  );

  const { switcher, currentTheme, themes } = useThemeSwitcher();

  // if no token, try getting one on mount
  useEffect(() => {
    if (!apiCweb.defaults.headers.common.Authorization) {
      doGetNewToken();
    }
  }, [doGetNewToken]);

  useEffect(() => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      switcher({ theme: themes.su });
    } else if (window.sessionStorage.getItem('appState') === 'timeMachine') {
      switcher({ theme: themes.dark });
    }
  }, []);

  if (project.error?.response?.status === 403) {
    return (
      <div css={styles.container}>
        <div css={styles.errorMessageContainer}>
          <p css={styles.errorMessage}>
            {intl.formatMessage({ id: 'project-not-available-for-user' })}
          </p>
        </div>
      </div>
    );
  }

  if (project.error?.response?.status === 404) {
    return (
      <div css={styles.container}>
        <div css={styles.errorMessageContainer}>
          <p css={styles.errorMessage}>
            {intl.formatMessage({ id: 'project-not-available-for-user' })}
          </p>
        </div>
      </div>
    );
  }

  if (getNewTokenMutation.error) {
    return (
      <div css={styles.container}>
        <Result
          status="error"
          title="Authentication failed"
          subTitle="Please retry. If the error persists, try reloading the page"
          extra={[
            <Button key="buy" onClick={() => doGetNewToken()}>
              Try again
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (getNewTokenMutation.isLoading || project.isLoading) {
    return (
      <div css={styles.container}>
        <Spin size="large" />
      </div>
    );
  }

  // avoid first render
  if (!apiCweb.defaults.headers.common.Authorization)
    return (
      <div css={styles.container}>
        <Result status="error" title="Authentication failed" />
      </div>
    );

  return (
    <div
      css={{
        height: '100%',
        animation: `${fadeInKeyframes} 0.4s ease-in`,
      }}>
      <AppContext.Provider value={appContextStateChanged}>
        <SidebarContext.Provider value={sidebarContextValue}>
          <LargeFilesContext.Provider value={largeFilesChanged}>
            <FileToViewProvider>
              <ModalsProvider>
                <FileProgressContext.Provider value={fileProgressChanged}>
                  <AboveNav />
                  <div
                    css={{
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row-reverse',
                      height: 'calc(100vh - 50px)',
                      [ipdMiniMediaQ]: {
                        height: 'calc(100vh - 50px)',
                      },
                      // [ipdProMediaQ]: {
                      //   height: 'calc(100vh - 120px)',
                      // },
                    }}>
                    {/* TODO: remove this */}
                    {window.sessionStorage.getItem('appState') ===
                    'superUser' ? (
                      <Switch>
                        <Route
                          path="/projects/:projectId/archive"
                          component={RightDisplaySu}
                        />
                        <Route
                          path="/projects/:projectId"
                          exact
                          component={RightDisplaySuHome}
                        />
                        <Route
                          path="/projects/:projectId"
                          component={RightDisplaySu}
                        />
                        <Redirect to="/" />
                      </Switch>
                    ) : (
                      <UploadContext.Provider value={uploadContextChanged}>
                        <ShowUploadDropZoneContext.Provider
                          value={setShowUploadZoneContextChanged}>
                          <Switch>
                            <Route
                              path="/projects/:projectId"
                              exact
                              component={RightSideViewHome}
                            />

                            <Route
                              path="/projects/:projectId"
                              component={RightDisplay}
                            />
                            <Redirect to="/" />
                          </Switch>
                        </ShowUploadDropZoneContext.Provider>
                      </UploadContext.Provider>
                    )}
                    <UploadContext.Provider value={uploadContextChanged}>
                      <ShowUploadDropZoneContext.Provider
                        value={setShowUploadZoneContextChanged}>
                        <LeftNav />
                      </ShowUploadDropZoneContext.Provider>
                    </UploadContext.Provider>
                  </div>
                  <UploadContext.Provider value={uploadContextChanged}>
                    {fileProgressContext.length > 0 && <FileProgressPanel />}
                  </UploadContext.Provider>
                </FileProgressContext.Provider>

                <ActionModalsContainer />

                <PrimaryViewerPreviewWrapper />
                {!__DEV__ && <CommitsModal />}
                {largeFilesContext.length > 0 && <LargeFilesModal />}
              </ModalsProvider>
            </FileToViewProvider>
          </LargeFilesContext.Provider>
        </SidebarContext.Provider>
      </AppContext.Provider>
    </div>
  );
};

export default ProjectsRoot;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessageContainer: {
    display: 'flex',
    width: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: theme.colors.red,
    fontSize: 24,
    width: '50%',
    textAlign: 'center',
    border: `1px solid ${theme.colors.red}`,
    backgroundColor: theme.colors.white,
    borderRadius: 9,
    padding: '20px 20px',
    boxShadow: '0px 0px 19px rgba(0, 0, 0, 0.07)',
  },
});
